import React, { useCallback } from 'react'
import { Configure, InstantSearch } from 'react-instantsearch-core'
import AlgoliaSearchInsights from 'search-insights'
import styled from 'styled-components/native'

import { CategoriesList } from 'features/search/components/CategoriesList/CategoriesList'
import { SearchHeader } from 'features/search/components/SearchHeader/SearchHeader'
import { SearchSuggestions } from 'features/search/components/SearchSuggestions/SearchSuggestions'
import { SEARCH_CATEGORIES_ANCHOR_ID } from 'features/search/constants'
import { useSearch } from 'features/search/context/SearchWrapper'
import { getSearchClient } from 'features/search/helpers/getSearchClient'
import { useSearchHistory } from 'features/search/helpers/useSearchHistory/useSearchHistory'
import { env } from 'libs/environment/env'
import { useFeatureFlag } from 'libs/firebase/firestore/featureFlags/useFeatureFlag'
import { RemoteStoreFeatureFlags } from 'libs/firebase/firestore/types'
import { useNetInfoContext } from 'libs/network/NetInfoWrapper'
import { OfflinePage } from 'libs/network/OfflinePage'
import { ScreenPerformance } from 'performance/ScreenPerformance'
import { useMeasureScreenPerformanceWhenVisible } from 'performance/useMeasureScreenPerformanceWhenVisible'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { useIsLandscape } from 'shared/useIsLandscape/useIsLandscape'
import { Form } from 'ui/components/Form'
import { Page } from 'ui/pages/Page'

const suggestionsIndex = env.ALGOLIA_SUGGESTIONS_INDEX_NAME

export const SearchLanding = () => {
  useMeasureScreenPerformanceWhenVisible(ScreenPerformance.SEARCH)

  const netInfo = useNetInfoContext()
  const { isFocusOnSuggestions } = useSearch()
  const { setQueryHistory, queryHistory, addToHistory, removeFromHistory, filteredHistory } =
    useSearchHistory()
  const enableNewCategoryBlocks = useFeatureFlag(RemoteStoreFeatureFlags.WIP_NEW_CATEGORY_BLOCKS)

  const isZoomedAt200 = useMobileFontScaleToDisplay({ default: false, at200PercentZoom: true })
  const isLandscape = useIsLandscape()
  const setQueryHistoryMemoized = useCallback(
    (query: string) => setQueryHistory(query),
    [setQueryHistory]
  )

  if (!netInfo.isConnected) {
    return <OfflinePage />
  }

  const searchHeader = (
    <Container>
      <SearchHeader
        shouldDisplaySubtitle
        addSearchHistory={addToHistory}
        searchInHistory={setQueryHistoryMemoized}
        quickAccess={{
          targetId: SEARCH_CATEGORIES_ANCHOR_ID,
          title: 'Aller aux catégories',
        }}
      />
    </Container>
  )
  const content = () => {
    if (isFocusOnSuggestions) {
      return (
        <React.Fragment>
          {isZoomedAt200 || isLandscape ? null : searchHeader}
          <SearchSuggestions
            queryHistory={queryHistory}
            addToHistory={addToHistory}
            removeFromHistory={removeFromHistory}
            filteredHistory={filteredHistory}
            shouldNavigateToSearchResults
            header={isZoomedAt200 || isLandscape ? searchHeader : undefined}
          />
        </React.Fragment>
      )
    }
    return (
      <React.Fragment>
        {isZoomedAt200 || isLandscape ? (
          <LandingScrollView keyboardShouldPersistTaps="handled">
            {searchHeader}
            <CategoriesButtonsContainer>
              <CategoriesList enableNewCategoryBlocks={enableNewCategoryBlocks} />
            </CategoriesButtonsContainer>
          </LandingScrollView>
        ) : (
          <React.Fragment>
            {searchHeader}
            <CategoriesButtonsContainer>
              <CategoriesList enableNewCategoryBlocks={enableNewCategoryBlocks} />
            </CategoriesButtonsContainer>
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }

  return (
    <Page>
      <Form.Flex>
        <InstantSearch
          future={{ preserveSharedStateOnUnmount: true }}
          searchClient={getSearchClient}
          indexName={suggestionsIndex}
          insights={{ insightsClient: AlgoliaSearchInsights }}>
          <Configure hitsPerPage={5} clickAnalytics analytics />

          {content()}
        </InstantSearch>
      </Form.Flex>
    </Page>
  )
}

const CategoriesButtonsContainer = styled.View(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  ...(theme.isMobileViewport ? { marginBottom: theme.tabBar.height } : {}),
}))

const LandingScrollView = styled.ScrollView(({ theme }) => ({
  flex: 1,
  ...(theme.isMobileViewport ? { marginBottom: theme.tabBar.height } : {}),
}))

const Container = styled.View(({ theme }) => ({
  marginBottom: theme.designSystem.size.spacing.s,
}))
